import { type FC, type PropsWithChildren } from 'react';

import { breakpointVariants, heightVars, skeletonChildStyle, skeletonRecipe, widthVars } from './Skeleton.css';
import { type DynamicVariantProp } from '../../types';
import { getClassNames } from '../../util/css';
import { extractDynamicStyles } from '../../util/css/vanilla-extract';
import { type RecipeVariants } from '../../vanilla-extract.css';

const sparkySkeletonClassName = 'sparky-skeleton';

export type SkeletonVariants = RecipeVariants<typeof skeletonRecipe>;

type SkeletonProps = {
  variant?: SkeletonVariants['variant'];
  width?: DynamicVariantProp<'width'>;
  height?: DynamicVariantProp<'height'>;
  className?: never;
};

export const Skeleton: FC<PropsWithChildren<SkeletonProps>> = ({
  variant = 'text',
  width,
  height,
  className = '',
  children,
}) => {
  const { classNames: dynamicCssClasses, cssVars } = extractDynamicStyles({
    height: [breakpointVariants.height, heightVars, height],
    width: [breakpointVariants.width, widthVars, width],
  });

  const classNames = getClassNames(
    sparkySkeletonClassName,
    skeletonRecipe({ variant, hasChildren: !!children, hasHeight: !!height }),
    ...dynamicCssClasses,
    className,
  );

  return (
    <div className={classNames} style={cssVars}>
      {children ? (
        <span className={skeletonChildStyle} aria-hidden>
          {children}
        </span>
      ) : null}
    </div>
  );
};

Skeleton.toString = () => `.${sparkySkeletonClassName}`;
Skeleton.displayName = 'Skeleton';
